import { useNavigate } from 'react-router-dom';
import { useSettings } from '@/contexts/SettingsContext';

export function TermsOfService() {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const language = settings?.language || 'en';
  const fr = language === 'fr';

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur-xl dark:border-slate-700 dark:bg-slate-900/95">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3 sm:px-6">
          <button onClick={() => navigate('/')} className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-purple-600">
              <span className="text-lg">💰</span>
            </div>
            <span className="font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              MyBalance
            </span>
          </button>
          <button
            onClick={() => navigate(-1)}
            className="text-sm font-medium text-slate-600 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 transition-colors"
          >
            {fr ? '← Retour' : '← Back'}
          </button>
        </div>
      </nav>

      <main className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <h1 className="text-3xl font-black text-slate-900 dark:text-white sm:text-4xl mb-2">
            {fr ? "Conditions d'utilisation" : 'Terms of Service'}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-10">
            {fr ? 'Dernière mise à jour : Février 2026' : 'Last updated: February 2026'}
          </p>

          <div className="space-y-8 text-slate-700 dark:text-slate-300">

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? "1. Acceptation des conditions" : '1. Acceptance of Terms'}
              </h2>
              <p className="leading-relaxed">
                {fr
                  ? "En créant un compte ou en utilisant MyBalance (le « Service »), vous acceptez d'être lié par ces conditions d'utilisation. Si vous n'acceptez pas, veuillez ne pas utiliser le Service. Ces conditions constituent un accord juridiquement contraignant entre vous et Unova."
                  : 'By creating an account or using MyBalance (the "Service"), you agree to be bound by these Terms of Service. If you do not agree, please do not use the Service. These terms constitute a legally binding agreement between you and Unova.'
                }
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '2. Description du service' : '2. Description of Service'}
              </h2>
              <p className="leading-relaxed">
                {fr
                  ? "MyBalance est un outil de suivi des finances personnelles permettant aux utilisateurs d'enregistrer des revenus, des dépenses, de définir des objectifs d'épargne et de générer des rapports. Le Service est actuellement fourni gratuitement pendant la période bêta publique. Unova se réserve le droit d'introduire des offres payantes à l'avenir, avec un préavis aux utilisateurs existants."
                  : "MyBalance is a personal finance tracking tool that allows users to record income, expenses, set savings goals, and generate reports. The Service is currently provided free of charge during the public beta period. Unova reserves the right to introduce paid tiers in the future, with advance notice to existing users."
                }
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '3. Comptes utilisateur' : '3. User Accounts'}
              </h2>
              <ul className="list-disc list-inside space-y-2 text-sm">
                {fr ? <>
                  <li>Vous devez fournir une adresse e-mail valide pour créer un compte.</li>
                  <li>Vous êtes responsable de la sécurité de votre mot de passe et de votre compte.</li>
                  <li>Vous devez avoir au moins 13 ans pour utiliser le Service.</li>
                  <li>Vous ne pouvez pas créer de comptes au nom d'autres personnes sans leur consentement.</li>
                  <li>Vous êtes responsable de toute activité qui se produit sous votre compte.</li>
                </> : <>
                  <li>You must provide a valid email address to create an account.</li>
                  <li>You are responsible for maintaining the security of your password and account.</li>
                  <li>You must be at least 13 years old to use the Service.</li>
                  <li>You may not create accounts on behalf of other people without their consent.</li>
                  <li>You are responsible for all activity that occurs under your account.</li>
                </>}
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '4. Utilisation acceptable' : '4. Acceptable Use'}
              </h2>
              <p className="leading-relaxed mb-3">
                {fr ? 'Vous acceptez de ne pas :' : 'You agree not to:'}
              </p>
              <ul className="list-disc list-inside space-y-2 text-sm">
                {fr ? <>
                  <li>Utiliser le Service à des fins illégales ou en violation de toute loi applicable.</li>
                  <li>Tenter d'obtenir un accès non autorisé à toute partie du Service ou de son infrastructure.</li>
                  <li>Utiliser des outils automatisés (bots, scrapers) pour accéder au Service ou l'abuser.</li>
                  <li>Usurper l'identité de toute personne ou entité.</li>
                  <li>Introduire des logiciels malveillants, des virus ou tout code nuisible.</li>
                </> : <>
                  <li>Use the Service for any illegal purpose or in violation of any applicable law.</li>
                  <li>Attempt to gain unauthorized access to any part of the Service or its infrastructure.</li>
                  <li>Use automated tools (bots, scrapers) to access or abuse the Service.</li>
                  <li>Impersonate any person or entity.</li>
                  <li>Introduce malware, viruses, or any harmful code.</li>
                </>}
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '5. Vos données et votre contenu' : '5. Your Data and Content'}
              </h2>
              <p className="leading-relaxed">
                {fr
                  ? "Toutes les données financières que vous saisissez dans MyBalance vous appartiennent. Vous accordez à Unova une licence limitée et non exclusive pour stocker et traiter vos données dans le seul but de fournir le Service. Nous ne revendiquons pas la propriété de vos données. Vous pouvez exporter ou supprimer vos données à tout moment."
                  : "All financial data you enter into MyBalance belongs to you. You grant Unova a limited, non-exclusive license to store and process your data solely for the purpose of providing the Service. We do not claim ownership over your data. You can export or delete your data at any time."
                }
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '6. Pas de conseil financier' : '6. No Financial Advice'}
              </h2>
              <p className="leading-relaxed">
                {fr
                  ? "MyBalance est uniquement un outil d'enregistrement et de suivi. Rien dans le Service ne constitue un conseil financier, d'investissement, juridique ou fiscal. Vous devriez consulter un professionnel financier qualifié pour des conseils adaptés à votre situation. Unova n'est pas responsable des décisions financières que vous prenez sur la base des données affichées dans l'application."
                  : "MyBalance is a record-keeping and tracking tool only. Nothing in the Service constitutes financial, investment, legal, or tax advice. You should consult a qualified financial professional for advice tailored to your specific situation. Unova is not liable for any financial decisions you make based on data displayed in the app."
                }
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '7. Avertissement bêta' : '7. Beta Service Disclaimer'}
              </h2>
              <p className="leading-relaxed">
                {fr
                  ? "MyBalance est actuellement en bêta publique. Le Service est fourni « tel quel » et peut contenir des bugs ou subir des interruptions. Nous ne garantissons pas la disponibilité, la rétention des données ou la disponibilité des fonctionnalités pendant la période bêta. Nous ferons des efforts raisonnables pour notifier les utilisateurs avant tout changement significatif ou panne."
                  : 'MyBalance is currently in public beta. The Service is provided "as is" and may contain bugs or experience downtime. We make no guarantees of uptime, data retention, or feature availability during the beta period. We will make reasonable efforts to notify users before any significant changes or outages.'
                }
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '8. Limitation de responsabilité' : '8. Limitation of Liability'}
              </h2>
              <p className="leading-relaxed">
                {fr
                  ? "Dans la mesure maximale permise par la loi, Unova ne sera pas responsable des dommages indirects, accessoires, spéciaux, consécutifs ou punitifs, y compris la perte de données ou les pertes financières, découlant de votre utilisation ou de l'impossibilité d'utiliser le Service."
                  : "To the maximum extent permitted by law, Unova shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of data or financial losses, arising from your use of or inability to use the Service."
                }
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '9. Résiliation' : '9. Termination'}
              </h2>
              <p className="leading-relaxed">
                {fr
                  ? "Vous pouvez cesser d'utiliser le Service à tout moment et demander la suppression de votre compte. Nous nous réservons le droit de suspendre ou de résilier les comptes qui violent ces conditions, avec ou sans préavis. En cas de résiliation, votre droit d'utiliser le Service cesse immédiatement."
                  : "You may stop using the Service at any time and request deletion of your account. We reserve the right to suspend or terminate accounts that violate these Terms, with or without notice. Upon termination, your right to use the Service ceases immediately."
                }
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '10. Modifications des conditions' : '10. Changes to Terms'}
              </h2>
              <p className="leading-relaxed">
                {fr
                  ? "Nous pouvons mettre à jour ces conditions de temps à autre. Nous vous informerons des changements significatifs par e-mail ou via une notice dans l'application au moins 14 jours avant leur entrée en vigueur. Votre utilisation continue du Service après la date d'entrée en vigueur constitue une acceptation des conditions mises à jour."
                  : "We may update these Terms from time to time. We will notify you of material changes via email or an in-app notice at least 14 days before they take effect. Your continued use of the Service after the effective date constitutes acceptance of the updated Terms."
                }
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '11. Droit applicable' : '11. Governing Law'}
              </h2>
              <p className="leading-relaxed">
                {fr
                  ? "MyBalance est développé et exploité par Unova, une équipe basée en Inde et à Madagascar. Ces conditions sont régies par les lois applicables et tout litige sera résolu de bonne foi. En utilisant le Service, vous acceptez que les tribunaux indiens aient une compétence non exclusive sur tout litige découlant de ces conditions."
                  : "MyBalance is built and operated by Unova, a team based in India and Madagascar. These Terms are governed by applicable laws, and any disputes will be resolved in good faith. By using the Service, you agree that the courts of India shall have non-exclusive jurisdiction over any disputes arising from these Terms."
                }
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                {fr ? '12. Contact' : '12. Contact'}
              </h2>
              <p className="leading-relaxed">
                {fr ? "Des questions sur ces conditions ? Contactez-nous à :" : 'Questions about these Terms? Contact us at:'}{' '}
                <a href="mailto:contact@mybalancya.com" className="text-blue-600 hover:underline dark:text-blue-400">
                  contact@mybalancya.com
                </a>
              </p>
            </section>

          </div>
        </div>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 mt-12 py-6 text-center text-xs text-slate-400">
        © 2026 Unova. {fr ? 'Tous droits réservés.' : 'All rights reserved.'}
      </footer>
    </div>
  );
}
